import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DURATIONS = { idle:2200, processing:2800, success:2400 };
const CYCLE     = ["idle","processing","success"];

const CONTENT = {
  pay:        { idle:"Send to Member",      proc:"Processing",       succ:"Payment Sent!",     idleHint:"Ready to transfer…",   procHint:"Securing your transaction…", succHint:"Transaction complete ✓", idleIcon:"bx bx-send",           procIcon:"bx bx-transfer-alt", amount:"$500" },
  request:    { idle:"Request from Member", proc:"Sending Request",  succ:"Request Sent!",     idleHint:"Preparing request…",   procHint:"Notifying the member…",      succHint:"Member notified ✓",      idleIcon:"bx bx-receipt",        procIcon:"bx bx-bell",         amount:"$500" },
  withdrawal: { idle:"Withdraw to Admin",   proc:"Submitting",       succ:"Request Submitted!",idleHint:"Ready to withdraw…",   procHint:"Sending to admin…",          succHint:"Admin notified ✓",       idleIcon:"bx bx-money-withdraw", procIcon:"bx bx-upload",       amount:"$250" },
};

/* ── IDLE ── */
const IdleScreen = ({ mode }) => {
  const c = CONTENT[mode] || CONTENT.pay;
  return (
    <motion.div className="pa-screen" key="idle"
      initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
      exit={{ opacity:0, y:-16 }} transition={{ duration:.4 }}>
      <motion.div className="pa-avatar"
        animate={{ scale:[1,1.08,1] }}
        transition={{ duration:2, repeat:Infinity, ease:"easeInOut" }}>
        <i className={c.idleIcon}></i>
      </motion.div>
      <p className="pa-screen-label">{c.idle}</p>
      <motion.div className="pa-amount"
        initial={{ scale:.8, opacity:0 }} animate={{ scale:1, opacity:1 }}
        transition={{ delay:.2, type:"spring", stiffness:200 }}>
        {c.amount}
      </motion.div>
      <div className="pa-pulse-dots">
        {[0,1,2].map(i=>(
          <motion.span key={i} className="pa-dot"
            animate={{ scale:[1,1.5,1], opacity:[.35,1,.35] }}
            transition={{ duration:1.2, repeat:Infinity, delay:i*.22 }} />
        ))}
      </div>
      <p className="pa-hint">{c.idleHint}</p>
    </motion.div>
  );
};

/* ── PROCESSING ── */
const ProcessingScreen = ({ mode }) => {
  const c = CONTENT[mode] || CONTENT.pay;
  return (
    <motion.div className="pa-screen" key="processing"
      initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
      exit={{ opacity:0, y:-16 }} transition={{ duration:.4 }}>
      <div className="pa-ring-wrap">
        <motion.div className="pa-ring"
          animate={{ rotate:360 }}
          transition={{ duration:1, repeat:Infinity, ease:"linear" }} />
        <motion.div className="pa-ring pa-ring2"
          animate={{ rotate:-360 }}
          transition={{ duration:1.6, repeat:Infinity, ease:"linear" }} />
        <div className="pa-ring-icon"><i className={c.procIcon}></i></div>
      </div>
      <p className="pa-proc-title">{c.proc}</p>
      <div className="pa-prog-track">
        <motion.div className="pa-prog-fill"
          initial={{ width:"0%" }} animate={{ width:"100%" }}
          transition={{ duration:2.4, ease:"easeInOut" }} />
      </div>
      <p className="pa-hint" style={{ color:"#93c5fd" }}>{c.procHint}</p>
    </motion.div>
  );
};

/* ── SUCCESS ── */
const SuccessScreen = ({ mode }) => {
  const c = CONTENT[mode] || CONTENT.pay;
  return (
    <motion.div className="pa-screen" key="success"
      initial={{ opacity:0, scale:.85 }} animate={{ opacity:1, scale:1 }}
      exit={{ opacity:0, scale:.85 }}
      transition={{ duration:.4, type:"spring", stiffness:180 }}>
      {[1,2,3].map(i=>(
        <motion.div key={i} className="pa-burst"
          initial={{ scale:.3, opacity:.8 }}
          animate={{ scale:i*1.2, opacity:0 }}
          transition={{ duration:.8, delay:i*.12, ease:"easeOut" }} />
      ))}
      <motion.div className="pa-check"
        initial={{ scale:0, rotate:-30 }} animate={{ scale:1, rotate:0 }}
        transition={{ type:"spring", stiffness:260, damping:16, delay:.15 }}>
        <i className="bx bx-check-circle"></i>
      </motion.div>
      <motion.p className="pa-success-title"
        initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
        transition={{ delay:.35 }}>{c.succ}</motion.p>
      <motion.div className="pa-success-amount"
        initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
        transition={{ delay:.45 }}>{c.amount}</motion.div>
      {[...Array(10)].map((_,i)=>(
        <motion.span key={i} className="pa-confetti"
          style={{ background:["#4ade80","#a78bfa","#60a5fa","#fbbf24","#f472b6"][i%5], top:"50%", left:"50%" }}
          initial={{ x:0, y:0, scale:0, opacity:1 }}
          animate={{ x:Math.cos(i*36*Math.PI/180)*(50+i*6), y:Math.sin(i*36*Math.PI/180)*(50+i*6), scale:[0,1,0], opacity:[1,1,0] }}
          transition={{ duration:.8, delay:.3+i*.04 }} />
      ))}
      <motion.p className="pa-hint" style={{ color:"#4ade80" }}
        initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.6 }}>
        {c.succHint}
      </motion.p>
    </motion.div>
  );
};

/* ── MAIN ── */
const PaymentAnimation = ({ mode = "pay" }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setStep(s => (s+1)%3), DURATIONS[CYCLE[step]]);
    return () => clearTimeout(id);
  }, [step]);

  useEffect(() => { setStep(0); }, [mode]);

  const phoneClass = `pa-phone ${step===1?"pa-phone-proc":""} ${step===2?"pa-phone-succ":""}`;

  const labelText  = [
    mode==="withdrawal" ? "Ready to Withdraw" : "Ready to Transfer",
    "Processing…",
    "Success!"
  ][step];

  const labelIcon  = [
    CONTENT[mode]?.idleIcon || "bx bx-send",
    "bx bx-loader-circle",
    "bx bx-check-circle"
  ][step];

  return (
    <div className="pa-wrap">
      <div className={phoneClass}>
        <div className="pa-notch" />
        <AnimatePresence mode="wait">
          {step===0 && <IdleScreen       key="idle" mode={mode} />}
          {step===1 && <ProcessingScreen key="proc" mode={mode} />}
          {step===2 && <SuccessScreen    key="succ" mode={mode} />}
        </AnimatePresence>
        <div className="pa-steps">
          {CYCLE.map((_,i)=>(
            <motion.span key={i} className={`pa-step-dot ${i===step?"active":""}`}
              animate={{ scale:i===step?1.3:1 }} transition={{ duration:.2 }} />
          ))}
        </div>
      </div>
      <motion.div className={`pa-label ${step===1?"proc":""} ${step===2?"succ":""}`}
        key={step} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
        transition={{ duration:.3 }}>
        <i className={labelIcon}></i> {labelText}
      </motion.div>
    </div>
  );
};

export default PaymentAnimation;
